import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { TileSize } from "game-common/models"
import {
    FOREST_DIRT_EDGE_START, FOREST_DIRT_TILESET, FOREST_DIRT_ZONES, LPC_DIRT_TILE, forestDirtSoilZone,
} from "game-common/room/ember/forest_dirt_art"
import { contentSheetResource, contentTextureGeneration } from "../content_textures"

/** The tile on a ground layer, live map edits included. */
export const groundTileAt = (provider: TileMapMetaProvider, x: number, y: number, layer: string): number => {
    const delta = provider.getMapDeltaAt(x, y, layer)
    return delta ? (delta.removed ? 0 : Number(delta.tileId))
        : Number(provider.resolveTileMapFromLayerId(layer)?.[x]?.[y] || 0)
}

const masks = new Map<number, Uint8Array>()
let maskGeneration = -1

/**
 * Which pixels of a forest-dirt EDGE overlay are soil; the rest is transparent, so the grass under it
 * shows. Read from the atlas once per tile, undefined until the sheet has loaded.
 */
export const forestDirtEdgeSoil = (position: number): Uint8Array | undefined => {
    const generation = contentTextureGeneration()
    if (generation !== maskGeneration) { masks.clear(); maskGeneration = generation }
    const cached = masks.get(position)
    if (cached) return cached
    const texture = contentSheetResource(FOREST_DIRT_TILESET)?.textures?.[`${FOREST_DIRT_TILESET}-${position + 1}`]
    const source = (texture?.baseTexture?.resource as any)?.source
    if (!texture || !source) return undefined
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = TileSize
    const context = canvas.getContext("2d")
    if (!context) return undefined
    const frame = texture.frame
    context.drawImage(source, frame.x, frame.y, frame.width, frame.height, 0, 0, TileSize, TileSize)
    const rgba = context.getImageData(0, 0, TileSize, TileSize).data
    const soil = new Uint8Array(TileSize * TileSize)
    for (let i = 0; i < soil.length; i++) soil[i] = rgba[i * 4 + 3] > 128 ? 1 : 0
    masks.set(position, soil)
    return soil
}

/** A forest-dirt edge overlay's position in its tileset, or undefined for any other overlay. */
export const forestDirtEdgePosition = (provider: TileMapMetaProvider, gid: number): number | undefined => {
    const sheet = provider.resolveTileSet(String(gid))
    const position = gid - (sheet?.firstGid ?? 0)
    return sheet?.tilesetId === FOREST_DIRT_TILESET && position >= FOREST_DIRT_EDGE_START ? position : undefined
}

const pixelIn = (p: number) => ((Math.floor(p) % TileSize) + TileSize) % TileSize

/** Whether the world pixel falls on the soil side of a forest-dirt edge overlay (false: the grass side). */
export const onForestDirtEdgeSoil = (position: number, px: number, py: number): boolean | undefined => {
    const soil = forestDirtEdgeSoil(position)
    return soil && soil[pixelIn(py) * TileSize + pixelIn(px)] === 1
}

/**
 * The soil zone (FOREST_DIRT_ZONES) of bare dirt at a world pixel, or undefined where the ground there is
 * not dirt. The ember's forest dirt has three zones by distance from the grass; the plain LPC dirt reads
 * as the middle one.
 */
export const dirtZoneAt = (provider: TileMapMetaProvider, px: number, py: number): number | undefined => {
    if (!provider) return undefined
    const x = Math.floor(px / TileSize), y = Math.floor(py / TileSize)
    const overlay = groundTileAt(provider, x, y, "map2")
    if (overlay) {
        const edge = forestDirtEdgePosition(provider, overlay)
        return edge !== undefined && onForestDirtEdgeSoil(edge, px, py) ? FOREST_DIRT_ZONES.edge : undefined
    }
    const gid = groundTileAt(provider, x, y, "map1")
    if (!gid) return undefined
    const sheet = provider.resolveTileSet(String(gid))
    if (!sheet) return undefined
    const position = gid - sheet.firstGid
    if (sheet.tilesetId === FOREST_DIRT_TILESET && position >= 0 && position < FOREST_DIRT_EDGE_START)
        return forestDirtSoilZone(position, pixelIn(px), pixelIn(py))
    if (sheet.tilesetId === "lpc-terrain" && position === LPC_DIRT_TILE) return FOREST_DIRT_ZONES.pebbly
    return undefined
}
